import { ActionIcon, Button, Checkbox, Combobox, Divider, Loader, ScrollArea, Stack, Text, TextInput, useCombobox } from "@mantine/core";
import { useDebouncedState, useDisclosure } from "@mantine/hooks";
import { ContextModalProps, modals, OpenConfirmModal } from "@mantine/modals";
import { WhatsappLogoIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import parsePhoneNumber from "libphonenumber-js";
import { ChangeEvent, ComponentProps, useState } from "react";
import { toast } from "react-toastify";

import Carregamento from "@/assets/images/Atendimento_Iniciado.svg";
import Cancelamento from "@/assets/images/Data_Cancelamento.svg";
import TruckIcon from "@/assets/images/icons/truck.svg";
import { useQueueUpdateCountAttendance } from "@/domain";
import { useSession } from "@/hooks/@custom/useSession";
import {
  QueueCompany,
  SaveQueueUpdateArgs,
  useGetQueueTotalsByCompanyQuery,
  useGetSettingsByCompanyQuery,
  useSaveCheckinCheckoutMutation,
  useSaveCheckinMutation,
  useSaveQueueCloseByIdMutation,
  useSaveQueueStartMutation,
  useSaveQueueUpdateDriverMutation,
  useSaveQueueUpdateMutation
} from "@/store/apis/nextcargo";
import { Driver, useListDriversByCompanyInfiniteQuery } from "@/store/apis/nextcargo/driver";
import { useLazyGetSchedulingsByDriverQuery, useSaveSchedulingMutation } from "@/store/apis/nextcargo/scheduling";
import { omitClassNameFromProps } from "@/utils";
import { cn } from "@/utils/cn";

import { Plate } from "../../Plate";
import { RoleGuard } from "../../RoleGuard";

type ExpeditionsShipmentModalProps = {
  queue: QueueCompany;
};
export function ExpeditionsShipmentModal({ context, id, innerProps }: ContextModalProps<ExpeditionsShipmentModalProps>) {
  const {
    queue: { checkinId: checkInId, ...queue }
  } = innerProps;

  const { expedition } = useSession();
  const company = expedition?.company.id;
  const expeditionCompanyId = Number(company);

  const [driver, setDriver] = useState<Driver>();
  const [debouncedDriverName, setDebouncedDriverName] = useDebouncedState("", 300);

  const { updateCountAttendance } = useQueueUpdateCountAttendance();
  const [saveCheckinCheckoutTrigger, { isLoading: isLoadingCheckinCheckout }] = useSaveCheckinCheckoutMutation();
  const [saveCheckinTrigger, { isLoading: isLoadingSaveCheckin }] = useSaveCheckinMutation();
  const [saveQueueCloseByIdTrigger, { isLoading: isLoadingQueueCloseById }] = useSaveQueueCloseByIdMutation();
  const [saveSchedulingTrigger, { isLoading: isLoadingSaveScheduling }] = useSaveSchedulingMutation();
  const [saveQueueUpdateTrigger, { isLoading: _isLoadingQueueUpdate }] = useSaveQueueUpdateMutation();
  const [saveQueueStartTrigger, { isLoading: isLoadingQueueStart }] = useSaveQueueStartMutation();
  const [saveQueueUpdateDriverTrigger, { isLoading: isLoadingQueueUpdateDriver }] = useSaveQueueUpdateDriverMutation();
  const [getSchedulingsByDriverTrigger, { isFetching: isLoadingGetSchedulingsByDriver }] = useLazyGetSchedulingsByDriverQuery();
  const { data: settings } = useGetSettingsByCompanyQuery({ id: expeditionCompanyId }, { skip: !expeditionCompanyId });
  const { data: queueStatus } = useGetQueueTotalsByCompanyQuery({ id: expeditionCompanyId }, { skip: !expeditionCompanyId });
  const {
    data: list,
    isFetching,
    isSuccess
  } = useListDriversByCompanyInfiniteQuery({
    id: expeditionCompanyId,
    params: {
      filter: debouncedDriverName,
      pageSize: 100,
      page: 0
    }
  });
  const data = list?.pages.flatMap((item) => item.data);
  const defaultDriverValue = data?.find((item) => String(item.name) === debouncedDriverName)?.name;

  const isLoadingShipping = isLoadingQueueStart || isLoadingQueueUpdateDriver;
  const isLoadingDimiss = isLoadingCheckinCheckout || isLoadingSaveCheckin || isLoadingQueueCloseById || isLoadingSaveScheduling;

  const handleChangeCombobox = (e: ChangeEvent<HTMLInputElement>) => {
    setDebouncedDriverName(e.currentTarget?.value);
    combobox.openDropdown();
    combobox.updateSelectedOptionIndex();
  };

  const handleOptionSubmit = (val: string) => {
    const driver = data?.find((item) => item.id === Number(val));
    if (!driver) return toast.warn("Motorista inexistente!");

    setDriver(driver);
    setDebouncedDriverName(driver?.name.trim() || "");
    combobox.closeDropdown();
  };

  /* ########################################################################## */

  const [checked, { toggle }] = useDisclosure(false);

  const companyId = company;
  const formattedForecastDate = dayjs(queue?.date).format("DD/MM [às] HH:mm");

  const driverHasContact = queue.driver?.contact?.[0];
  const phone = parsePhoneNumber(queue.driver?.contact[0]?.fone ?? "", "BR");
  const phoneFormatted = phone?.formatNational() ?? "";
  const productDescription = queue.scheduling?.order?.orderItem
    .reduce((acc, item) => {
      return acc + item.product.name + ", ";
    }, "")
    .slice(0, -2);
  const wpUrl = `https://api.whatsapp.com/send?phone=${phone?.number}&text=Olá+motorista+${queue.driver?.name.trimEnd()},+estamos+em+contato+para+informar+sobre+o+atendimento+do+veículo+${queue.truck?.licensePlate}.`;
  const isToday = dayjs(queue?.date).isSame(dayjs(), "day");

  const shouldFilterOptions = !data?.some((item) => item.id === driver?.id);
  const filteredOptions = shouldFilterOptions ? data?.filter((item) => item.name.toLowerCase().includes(debouncedDriverName.toLowerCase().trim())) : data;
  const hasDimiss = !settings?.enableTolerance && isToday;

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption()
  });

  const options = filteredOptions?.map((item) => (
    <Combobox.Option value={String(item.id)} key={item.id}>
      {item.name}
    </Combobox.Option>
  ));

  const handleDimiss = async () => {
    const queueId = queue.id;
    const driverId = queue.driver?.id;
    const schedulingId = queue.scheduling?.id;

    const createScheduling = async () => {
      if (!schedulingId) return;

      await saveSchedulingTrigger({
        id: schedulingId,
        status: false
      });
    };

    const createCloseQueue = async () => {
      if (!queueId) return;

      await saveQueueCloseByIdTrigger({
        id: queueId
      });
    };

    const cancelCheckIn = async () => {
      if (!queueId || !driverId || !companyId) return;

      await saveCheckinTrigger({
        id: driverId,
        company: { id: Number(companyId) },
        queue: { id: queueId },
        isPresent: false,
        status: false
      });
    };

    const createLogCheckout = async () => {
      if (!checkInId || !driverId) return;

      await saveCheckinCheckoutTrigger({
        id: checkInId,
        driver: {
          id: driverId
        }
      });
    };

    try {
      const promises = [createScheduling(), createCloseQueue(), cancelCheckIn(), createLogCheckout()] as const;
      await Promise.all(promises);

      context.closeModal(id);
    } catch {
      toast.error("Erro ao cancelar a posição na fila!");
    }
  };

  const handleCancel = () => {
    if (!isToday) {
      toast.error("Apenas agendamentos do dia de hoje podem ser cancelados");
      return;
    }

    customConfirmModal({
      onConfirm: handleDimiss,
      children: (
        <ContentConfirmModal plate={queue.truck?.licensePlate} src={Cancelamento}>
          Deseja cancelar a posição na fila?
        </ContentConfirmModal>
      )
    });
  };

  const handleAccept = () => {
    customConfirmModal({
      onConfirm: async () => {
        const body = {
          id: queue.id,
          driver: {
            id: Number(queue.driver?.id)
          },
          company: {
            id: companyId!
          }
        };

        const promises = [
          saveQueueUpdateDriverTrigger(body),
          saveQueueStartTrigger({
            id: body.id,
            status: true
          })
        ] as const;

        try {
          const [_updateDriverResponse, startQueueResponse] = await Promise.allSettled(promises);

          if (startQueueResponse.status === "fulfilled") {
            updateCountAttendance(Number(companyId));
          }

          context.closeModal(id);
        } catch {
          toast.error("Erro ao verificar disponibilidade do carregamento");
        }
      },
      children: (
        <ContentConfirmModal plate={queue.truck?.licensePlate} src={Carregamento}>
          Deseja iniciar <br /> o carregamento?
        </ContentConfirmModal>
      ),
      classNames: { content: "!bg-green-600" },
      confirmProps: {
        disabled: isLoadingDimiss,
        loading: isLoadingShipping
      },
      cancelProps: {
        disabled: isLoadingShipping,
        loading: isLoadingDimiss
      }
    });
  };

  const handleStartShipping = async () => {
    if (!queue.driver?.id) return;
    const limit = settings?.programmedTotal || 0;
    const attended = queueStatus?.attended || 0;

    try {
      const schedulings = await getSchedulingsByDriverTrigger({ id: queue.driver.id }).unwrap();

      if (limit && limit < attended) return toast.error("Limite de atendimento atingido!");

      const schedulingId = schedulings[0].id;
      if (!schedulingId) return toast.error("Adicione o veículo e o Motorista em um pedido!");

      const body: SaveQueueUpdateArgs = {
        id: queue.id,
        scheduling: { id: schedulingId },
        company: companyId ? { id: +companyId } : null,
        driver: { id: queue.driver.id }
      };

      await saveQueueUpdateTrigger(body);
      handleAccept();
    } catch {
      toast.error("Adicione o veículo e o Motorista em um pedido!");
    }
  };

  return (
    <Stack gap="xs" className="pt-2">
      <div className="flex flex-col gap-[inherit]">
        <div className="grid grid-cols-1 place-items-center gap-0 leading-none md:flex md:flex-row md:items-center md:justify-center md:gap-[inherit] md:leading-normal">
          <Text className="text-lg! md:text-xs!">
            Previsão de <br className="hidden md:block" /> atendimento
          </Text>

          <time className="text-2xl font-semibold">{formattedForecastDate}</time>
        </div>

        <Divider color="gray" variant="dashed" size="xs" />

        <div className="flex items-center justify-center gap-[inherit] text-red-500">
          {queue?.truck?.licensePlate && <Plate plate={queue.truck.licensePlate} color="text-current" />}

          {queue.truck?.vehicleModel && (
            <div className="flex flex-col gap-1 text-xs leading-none font-semibold">
              <p>{queue.truck.vehicleModel.name}</p>
              <p>{queue.truck.vehicleModel.truckBrand?.name}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-3">
          <InfoItem label="Motorista:" slots={{ wrapper: { className: cn("md:col-span-2") } }}>
            {queue.driver?.name}
          </InfoItem>

          {driverHasContact && (
            <RoleGuard notAllowed={[RoleGuard.Role.Expedicao, RoleGuard.Role.Portaria]}>
              <InfoItem label="Telefone:" slots={{ wrapper: { className: cn("md:col-span-1") }, children: { className: "flex items-center gap-2" } }}>
                {phoneFormatted}{" "}
                <ActionIcon component="a" href={wpUrl} target="_blank" rel="noopener noreferrer" radius="xs" size="xs" color="green" variant="transparent" p={0}>
                  <WhatsappLogoIcon weight="duotone" />
                </ActionIcon>
              </InfoItem>
            </RoleGuard>
          )}
        </div>

        {queue.scheduling && (
          <>
            <Divider color="gray" variant="dashed" size="xs" />

            {(queue.scheduling?.order?.customer?.name || queue.scheduling?.order?.customer?.city) && (
              <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-3">
                <InfoItem label="Destinatário:" slots={{ wrapper: { className: cn("md:col-span-2") } }}>
                  {queue.scheduling.order.customer.name}
                </InfoItem>

                <InfoItem label="Município:" slots={{ wrapper: { className: cn("md:col-span-1") } }}>
                  {queue.scheduling.order.customer.city}
                </InfoItem>
              </div>
            )}

            <Divider color="gray" variant="dashed" size="xs" />

            {(queue.scheduling?.order?.shippingCompany[0]?.name || productDescription) && (
              <div className="flex flex-col gap-[inherit]">
                <InfoItem label="Transportadora:">{queue.scheduling.order?.shippingCompany[0]?.name}</InfoItem>
                <InfoItem label="Produto:">{productDescription}</InfoItem>
              </div>
            )}
          </>
        )}

        {!queue.scheduling && (
          <>
            <Divider color="gray" variant="dashed" size="xs" />

            <div className="flex items-center justify-center gap-1">
              <img src={TruckIcon} className="size-5 invert" />
              <p className="text-sm">
                Motorista <strong>Autônomo</strong>
              </p>
            </div>
          </>
        )}

        <Divider color="gray" variant="dashed" size="xs" />

        <RoleGuard notAllowed={[RoleGuard.Role.Expedicao, RoleGuard.Role.Portaria]}>
          <div className="flex flex-col gap-[inherit]">
            <Checkbox label="Troca de motorista" color="blue" checked={checked} onChange={toggle} size="sm" radius="sm" />

            {checked && (
              <Stack gap="xs">
                <Combobox store={combobox} withinPortal={false} onOptionSubmit={handleOptionSubmit} position="top" size="sm" radius="sm">
                  <Combobox.Target>
                    <TextInput
                      label="Selecione um motorista"
                      placeholder={isFetching ? "Carregando..." : "Digite o nome do motorista"}
                      rightSection={isFetching ? <Loader size="sm" /> : <Combobox.Chevron />}
                      defaultValue={defaultDriverValue}
                      onChange={handleChangeCombobox}
                      onClick={() => combobox.openDropdown()}
                      onFocus={() => combobox.openDropdown()}
                      onBlur={() => combobox.closeDropdown()}
                      size="sm"
                      radius="sm"
                    />
                  </Combobox.Target>

                  <Combobox.Dropdown>
                    <Combobox.Options>
                      <ScrollArea.Autosize mah={200} type="scroll">
                        {isSuccess && !isFetching && options && (
                          <>
                            {options.length === 0 && <Combobox.Empty>Nada encontrado</Combobox.Empty>}
                            {options.length > 0 && <Combobox.Group label="Motoristas">{options}</Combobox.Group>}
                          </>
                        )}

                        {!isFetching && !isSuccess && <Combobox.Empty c="red">Erro ao carregar motoristas!</Combobox.Empty>}
                        {isFetching && <Combobox.Empty c="blue">Carregando...</Combobox.Empty>}
                      </ScrollArea.Autosize>
                    </Combobox.Options>
                  </Combobox.Dropdown>
                </Combobox>

                {driver?.name && (
                  <Text size="sm" fw="bold">
                    Motorista Selecionado: <br />
                    <Text fw="normal">{driver.name}</Text>
                  </Text>
                )}
              </Stack>
            )}
          </div>
        </RoleGuard>
      </div>

      <RoleGuard notAllowed={[RoleGuard.Role.Expedicao, RoleGuard.Role.Portaria]}>
        <div className={cn("grid grid-cols-1 gap-[inherit] md:mt-2 md:grid-cols-2", { "md:grid-cols-1": !(!settings?.enableTolerance && isToday) })}>
          {hasDimiss && (
            <Button color="red" onClick={handleCancel} radius="sm" size="sm" disabled={isLoadingShipping || isLoadingGetSchedulingsByDriver} loading={isLoadingDimiss}>
              Cancelar Carregamento
            </Button>
          )}

          <RoleGuard notAllowed={[RoleGuard.Role.Transportadora]}>
            <Button color="green" onClick={handleStartShipping} radius="sm" size="sm" disabled={isLoadingDimiss} loading={isLoadingShipping || isLoadingGetSchedulingsByDriver}>
              Iniciar
            </Button>
          </RoleGuard>
        </div>
      </RoleGuard>
    </Stack>
  );
}

type InfoItemProps = {
  label: string;
  children: React.ReactNode;
  slots?: {
    wrapper?: ComponentProps<"div">;
    label?: ComponentProps<"p">;
    children?: ComponentProps<"p">;
  };
};
function InfoItem({ label, children, slots }: InfoItemProps) {
  if (!children || !label) return null;

  return (
    <div className={cn("flex flex-col text-xs text-gray-400", slots?.wrapper?.className)} {...omitClassNameFromProps(slots?.wrapper ?? {})}>
      <p className={cn("font-semibold text-gray-900", slots?.label?.className)} {...omitClassNameFromProps(slots?.label ?? {})}>
        {label}
      </p>
      <p className={cn("font-medium text-gray-600", slots?.children?.className)} {...omitClassNameFromProps(slots?.children ?? {})}>
        {children}
      </p>
    </div>
  );
}

type ContentConfirmModalProps = {
  src: string;
  children: React.ReactNode;
  plate?: string;
  slots?: { img?: ComponentProps<"img"> };
};
function ContentConfirmModal({ src, plate, children, slots }: ContentConfirmModalProps) {
  return (
    <div className="flex flex-col items-center gap-4 p-2">
      <img src={src} className={cn("w-[40%]", slots?.img?.className)} {...omitClassNameFromProps(slots?.img ?? {})} loading="eager" fetchPriority="high" />

      <div className="flex max-w-md flex-col items-center justify-center gap-6 text-center text-3xl text-white">
        {plate && <Plate plate={plate} slots={{ wrapper: { className: cn("bg-transparent") } }} color="text-white" />}
        <p className="font-semibold">{children}</p>
      </div>
    </div>
  );
}

const customConfirmModal = (props: OpenConfirmModal) => {
  const { classNames, confirmProps, cancelProps, ...rest } = props;

  const cancelPropsWithDefaults = {
    variant: "outline",
    color: "white",
    radius: "sm",
    ...omitClassNameFromProps(cancelProps ?? {})
  };
  const confirmPropsWithDefaults = {
    radius: "sm",
    variant: "white",
    classNames: {
      label: cn("text-black!")
    },
    ...omitClassNameFromProps(confirmProps ?? {})
  };

  modals.openConfirmModal({
    withCloseButton: false,
    fullScreen: true,
    classNames: {
      //@ts-ignore
      content: cn("bg-red-600! *:grid! *:min-h-dvh! *:place-items-center!", classNames?.content)
    },
    labels: {
      cancel: "Não",
      confirm: "Sim"
    },
    groupProps: {
      className: cn("items-center! justify-center!")
    },
    cancelProps: cancelPropsWithDefaults,
    confirmProps: confirmPropsWithDefaults,
    ...rest
  });
};
